import { defineComponent, h, useAttrs } from 'vue'
import VSelectCompat from './VSelectCompat'
import { normalizeLegacyMenuProps } from './legacyInputProps'

function flattenClasses(classes) {
  if (classes == null) return []
  if (typeof classes === 'string') return classes.split(/\s+/)
  if (Array.isArray(classes)) return classes.flatMap(flattenClasses)
  if (typeof classes === 'object') {
    return Object.entries(classes)
      .filter(([, enabled]) => Boolean(enabled))
      .map(([className]) => className)
  }
  return []
}

function getModelValue(attrs) {
  return attrs.modelValue ?? attrs.value
}

function getSelectableItems(attrs) {
  return Array.isArray(attrs.items)
    ? attrs.items.filter((item) => item !== '__ALL__')
    : []
}

function callLegacyHandler(handler, event) {
  if (Array.isArray(handler)) {
    handler.forEach((fn) => callLegacyHandler(fn, event))
    return
  }

  if (typeof handler === 'function') {
    handler(event)
  }
}

function openLegacyOverflowMenu(event) {
  const field = event.target?.closest?.('.v-field')

  if (!field || field.getAttribute('aria-expanded') === 'true') return

  window.setTimeout(() => {
    const input = field.querySelector?.('input[role="combobox"]')

    if (!input) return

    input.focus()
    input.dispatchEvent(
      new KeyboardEvent('keydown', {
        bubbles: true,
        cancelable: true,
        code: 'ArrowDown',
        key: 'ArrowDown',
        keyCode: 40,
        which: 40
      })
    )
  }, 0)
}

export function resolveLegacyOverflowSelection(attrs = {}) {
  const classes = flattenClasses(attrs.class)
  const modelValue = getModelValue(attrs)
  const selectedCount = Array.isArray(modelValue) ? modelValue.length : 0
  const totalCount = getSelectableItems(attrs).length

  if (classes.includes('p-btn-status--available')) {
    return {
      label: 'Available'
    }
  }

  if (classes.includes('p-btn-status--partially-available')) {
    return {
      label: 'Available',
      count: totalCount ? `(${selectedCount} of ${totalCount})` : undefined
    }
  }

  if (classes.includes('p-btn-status--unavailable')) {
    return {
      label: 'Unavailable'
    }
  }

  return undefined
}

export function normalizeLegacyOverflowMenuProps(attrs = {}) {
  const menuProps = normalizeLegacyMenuProps({
    ...attrs,
    attach: undefined
  })

  return {
    ...menuProps,
    contentClass: menuProps?.contentClass ?? attrs['menu-props'] ?? 'rounded',
    location: menuProps?.location ?? 'bottom start',
    origin: menuProps?.origin ?? 'top start',
    offset: menuProps?.offset ?? 0
  }
}

function stripOverflowOnlyAttrs(attrs = {}) {
  const normalized = { ...attrs }
  delete normalized.attach
  return normalized
}

export default defineComponent({
  name: 'VOverflowBtnCompat',
  compatConfig: {
    RENDER_FUNCTION: false,
    COMPONENT_V_MODEL: false,
    INSTANCE_ATTRS_CLASS_STYLE: false
  },
  inheritAttrs: false,
  emits: ['update:modelValue', 'change', 'input'],

  setup(_, { emit, slots }) {
    const attrs = useAttrs()

    return () => {
      const legacySelection = resolveLegacyOverflowSelection(attrs)
      const normalizedAttrs = stripOverflowOnlyAttrs(attrs)
      const forwardedSlots = {
        ...slots,
        selection: () => []
      }

      delete normalizedAttrs.onChange
      delete normalizedAttrs.onInput
      delete normalizedAttrs['onUpdate:modelValue']

      return h(
        VSelectCompat,
        {
          ...normalizedAttrs,
          class: ['v-overflow-btn', normalizedAttrs.class],
          menuProps: normalizeLegacyOverflowMenuProps(attrs),
          onClick: (event) => {
            callLegacyHandler(attrs.onClick, event)
            openLegacyOverflowMenu(event)
          },
          'onUpdate:modelValue': (value) => {
            emit('update:modelValue', value)
            emit('change', value)
            emit('input', value)
          }
        },
        {
          ...forwardedSlots,
          'prepend-inner': legacySelection
            ? (...slotArgs) => [
                h(
                  'div',
                  {
                    class:
                      'v-select__selection text-no-wrap v-overflow-btn__legacy-selection'
                  },
                  [
                    legacySelection.label,
                    legacySelection.count
                      ? h(
                          'span',
                          {
                            class:
                              'availability-status-count grey--text text--darken-1'
                          },
                          ` ${legacySelection.count}`
                        )
                      : undefined
                  ]
                )
              ]
            : slots['prepend-inner']
        }
      )
    }
  }
})
